import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Loader2, AlertTriangle } from 'lucide-react';
import Logo from '../components/Logo';

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function AuthCallback() {
  const nav = useNavigate();
  const { setUser } = useAuth();
  const processed = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Guard against React StrictMode double-invoke — session_id can only be
    // exchanged ONCE. Use useRef (not useState) so the flag is set
    // synchronously before the async task runs.
    if (processed.current) return;
    processed.current = true;
    const hash = window.location.hash || '';
    const m = hash.match(/session_id=([^&]+)/);
    if (!m) {
      // No session fragment — either the user landed here manually or the
      // Emergent Auth redirect dropped the hash. Send them back to /login.
      nav('/login', { replace: true });
      return;
    }
    const sid = m[1];
    (async () => {
      try {
        const { data } = await api.post('/auth/session', { session_id: sid });
        setUser(data);
        // Single navigation — `replace: true` swaps the URL from
        // `/app/planets/p5#session_id=…` to `/app/planets/p5` without leaving
        // a Back-stack entry, and unlike a bare `history.replaceState`, it
        // re-renders the matching Route. Lover's Nebula (p5) is the default
        // landing room for every sign-in.
        api.post('/planets/p5/join').catch(() => {});
        nav('/app/planets/p5', { replace: true });
      } catch (e) {
        // Surface the reason instead of silently ping-ponging back to /login
        // with no explanation — a blank/redirect screen was the #1 complaint
        // and made this the hardest bug to reproduce.
        console.error('[AuthCallback] session exchange failed:', e?.response?.status, e?.response?.data || e?.message);
        const detail = e?.response?.data?.detail;
        setError(typeof detail === 'string' ? detail : (e?.message || 'Google session could not be established.'));
      }
    })();
  }, [nav, setUser]);

  if (error) {
    return (
      <div className="min-h-screen cosmic-bg flex items-center justify-center px-4" data-testid="auth-callback-error">
        <div className="stars-bg" />
        <div className="relative max-w-md w-full rounded-3xl border border-red-400/25 bg-[#161622]/95 backdrop-blur-xl p-6 text-center">
          <div className="mx-auto mb-3 w-12 h-12 rounded-full bg-red-500/12 border border-red-400/40 flex items-center justify-center">
            <AlertTriangle size={22} className="text-red-300" />
          </div>
          <h2 className="font-display font-bold text-xl text-white">Sign-in interrupted</h2>
          <p className="mt-2 text-sm text-white/70 leading-relaxed">
            We couldn&apos;t complete your Google sign-in. This usually means your session expired,
            third-party cookies are blocked, or the network dropped mid-request.
          </p>
          <div className="mt-3 text-[11px] text-white/40 font-mono break-words">{error}</div>
          <Link
            to="/login"
            replace
            data-testid="auth-callback-back"
            className="mt-6 inline-block px-6 py-2.5 rounded-full font-semibold text-sm bg-gradient-to-r from-fuchsia-500 to-cyan-500 text-white hover:opacity-90 transition"
          >
            Back to sign in
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen cosmic-bg flex items-center justify-center" data-testid="auth-callback-loading">
      <div className="stars-bg" />
      <div className="relative flex flex-col items-center gap-4">
        <Logo size="lg" animate />
        <Loader2 className="animate-spin text-cyan-400" size={28} />
        <div className="text-white/60 text-sm">Linking your Google account...</div>
      </div>
    </div>
  );
}
